import { getCustomizationData, setCustomizationData, updateCustomizationData } from 'helpers/utils/customizationStorage';
import { sdk } from 'sdk';

/**
 * Single entry point for the Mediaclip user token.
 *
 */

const TOKEN_TTL = 10 * 60 * 1000;
// Treat a token as expired slightly early so it is never handed out mid-flight.
const EXPIRY_SKEW = 60 * 1000;

let tokenPromise: Promise<string | null> | null = null;

const readStoredToken = (): string | null => {
  const data = getCustomizationData();
  if (!data?.token || !data?.expiresAt) return null;
  if (Date.now() > data.expiresAt - EXPIRY_SKEW) return null;

  return data.token as string;
};

const persistToken = (token: string) => {
  setCustomizationData({ ...getCustomizationData(), token, expiresAt: Date.now() + TOKEN_TTL });
};

/** Drops the token everywhere so the next `getMediaclipToken()` mints a new one. */
export const clearMediaclipToken = () => {
  tokenPromise = null;

  updateCustomizationData((data) => {
    const next = { ...data };
    delete next.token;
    delete next.expiresAt;
    return next;
  });
};

export const getMediaclipToken = async (forceRefresh = false): Promise<string | null> => {
  if (!forceRefresh) {
    const storedToken = readStoredToken();
    if (storedToken) return storedToken;

    // A request is already on the wire — join it instead of starting a second one.
    if (tokenPromise) return tokenPromise;
  }

  tokenPromise = sdk.composableCommerce.mediaClip
    .getUserToken()
    .then((res) => {
      if (!res.isError && res.data?.userToken) {
        persistToken(res.data.userToken);
        return res.data.userToken;
      }
      return null;
    })
    .catch((error) => {
      console.error('Error fetching Mediaclip token:', error);
      return null;
    })
    .finally(() => {
      tokenPromise = null;
    });

  return tokenPromise;
};

/** Test-only: reset the module cache between cases. */
export const __resetMediaclipTokenCache = () => {
  tokenPromise = null;
};
